import SunAccent from "@/components/feature/SunAccent";
import { useState, type FormEvent } from "react";
import Reveal from "@/components/base/Reveal";

type Status = "idle" | "loading" | "success" | "error";

export default function NewsletterSection() {
  const [status, setStatus] = useState<Status>("idle");
  const [message, setMessage] = useState("");

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const formData = new FormData(form);

    const honeypot = String(formData.get("website_alt") || "").trim();
    if (honeypot) {
      setStatus("success");
      setMessage("Thank you! You are now subscribed to Rythugaru updates.");
      form.reset();
      return;
    }
    formData.delete("website_alt");

    const payload = new URLSearchParams();
    formData.forEach((value, key) => {
      payload.append(key, value.toString());
    });

    setStatus("loading");
    setMessage("");

    try {
      const response = await fetch("https://readdy.ai/api/form/dalr9sdh599pccsgttf0", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: payload.toString(),
      });

      const responseText = await response.text();
      let parsed: {
        code?: string;
        message?: string;
        meta?: { message?: string; detail?: string };
      } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      const serverMsg =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;
      const isSpam =
        typeof serverMsg === "string" && serverMsg.toLowerCase().includes("spam");

      if (response.ok && parsed?.code === "OK" && !isSpam) {
        setStatus("success");
        setMessage("Thank you! You are now subscribed to Rythugaru updates.");
        form.reset();
      } else {
        setStatus("error");
        setMessage(
          typeof serverMsg === "string" && serverMsg
            ? serverMsg
            : "Something went wrong. Please try again.",
        );
      }
    } catch {
      setStatus("error");
      setMessage("Network error. Please check your connection and try again.");
    }
  };

  return (
    <section id="subscribe" className="sun-glaze bg-accent-50 py-20 md:py-28">
      <div className="w-full px-4 md:px-8">
        <Reveal>
          <div className="bg-background-50 rounded-lg border border-accent-200 p-8 md:p-14 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-6">
              <div className="mb-4">
                <SunAccent circleClassName="w-8 h-8" iconClassName="text-base" />
              </div>
              <span className="text-xs font-semibold tracking-[0.24em] uppercase text-accent-700">
                Stay Connected
              </span>
              <h2 className="font-heading text-3xl md:text-4xl leading-tight text-foreground-950 font-semibold mt-4">
                Stories, Farms &amp; Experiences — Delivered to You
              </h2>
              <p className="mt-5 text-base text-foreground-600 leading-relaxed">
                Discover new farms, seasonal experiences, farmer stories, rural destinations, and
                upcoming events.
              </p>
              <div className="flex flex-wrap gap-x-6 gap-y-3 mt-7">
                {["New farms", "Seasonal experiences", "Farmer stories", "Upcoming events"].map(
                  (item) => (
                    <span key={item} className="flex items-center gap-2 text-sm text-foreground-700">
                      <i className="ri-checkbox-circle-fill text-accent-500"></i>
                      {item}
                    </span>
                  ),
                )}
              </div>
            </div>

            <div className="lg:col-span-6">
              <form
                id="rythugaru-newsletter-form"
                data-readdy-form
                onSubmit={handleSubmit}
                className="space-y-4"
              >
                <div>
                  <label htmlFor="nl-name" className="block text-sm font-medium text-foreground-800 mb-2">
                    Your name
                  </label>
                  <input
                    id="nl-name"
                    name="name"
                    type="text"
                    required
                    placeholder="e.g. Ananya Sharma"
                    className="w-full bg-background-100 border border-background-300 rounded-md px-4 py-3 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-accent-400 focus:border-accent-400"
                  />
                </div>
                <div>
                  <label htmlFor="nl-email" className="block text-sm font-medium text-foreground-800 mb-2">
                    Email address
                  </label>
                  <input
                    id="nl-email"
                    name="email"
                    type="email"
                    required
                    placeholder="you@example.com"
                    className="w-full bg-background-100 border border-background-300 rounded-md px-4 py-3 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-accent-400 focus:border-accent-400"
                  />
                </div>

                <div className="form-extra-contact" aria-hidden="true">
                  <label htmlFor="nl-website-alt">Leave this field empty</label>
                  <input
                    id="nl-website-alt"
                    name="website_alt"
                    type="text"
                    tabIndex={-1}
                    autoComplete="off"
                    aria-hidden="true"
                    readOnly
                  />
                </div>

                <button
                  type="submit"
                  disabled={status === "loading"}
                  className="w-full inline-flex items-center justify-center gap-2 bg-accent-500 text-background-50 text-sm font-semibold px-6 py-4 rounded-md hover:bg-accent-600 transition-colors whitespace-nowrap cursor-pointer disabled:opacity-70 disabled:cursor-not-allowed"
                >
                  {status === "loading" ? "Subscribing..." : "Subscribe to Rythugaru"}
                  {status !== "loading" && <i className="ri-send-plane-fill"></i>}
                </button>

                {status === "success" && (
                  <p className="flex items-start gap-2 text-sm text-primary-700 bg-primary-50 border border-primary-200 rounded-md px-4 py-3">
                    <i className="ri-checkbox-circle-fill text-lg"></i>
                    <span>{message}</span>
                  </p>
                )}
                {status === "error" && (
                  <p className="flex items-start gap-2 text-sm text-accent-800 bg-accent-100 border border-accent-300 rounded-md px-4 py-3">
                    <i className="ri-error-warning-fill text-lg"></i>
                    <span>{message}</span>
                  </p>
                )}
                <p className="text-xs text-foreground-500">
                  We respect your privacy. Unsubscribe anytime.
                </p>
              </form>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
}